import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavGroup, NavItem } from '@/components/layoutNav';

type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
  navGroups: NavGroup[];
};

function flattenNavItems(groups: NavGroup[]): NavItem[] {
  return groups.flatMap((group) =>
    group.items.map((item) => item),
  );
}

export default function CommandPalette({ open, onClose, navGroups }: CommandPaletteProps) {
  const dialogId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const items = useMemo(() => flattenNavItems(navGroups), [navGroups]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 12);
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.href.toLowerCase().includes(q) ||
        item.context.includes(q),
    );
  }, [items, query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="command-palette-backdrop" role="presentation" onClick={onClose}>
      <div
        className="command-palette"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        onClick={(event) => event.stopPropagation()}
        data-testid="command-palette"
      >
        <h2 id={`${dialogId}-title`} className="command-palette__title">
          Go to…
        </h2>
        <input
          ref={inputRef}
          className="command-palette__input"
          type="search"
          placeholder="Search pages (Ctrl+K)"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search navigation"
        />
        <ul className="command-palette__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="command-palette__empty muted">No matches</li>
          ) : (
            filtered.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="command-palette__item"
                  role="option"
                  onClick={() => {
                    navigate(item.href);
                    onClose();
                  }}
                >
                  <span className="command-palette__item-label">{item.label}</span>
                  <span className="command-palette__item-meta muted">{item.href}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
